import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Loader2, Folder, FileText, CheckCircle2, ShieldAlert, AlertCircle, XCircle, Copy, IndianRupee } from 'lucide-react';
import { IdeasFilterTable } from '../components/IdeasFilterTable.tsx';
import { AmountDisplay } from '../components/AmountDisplay.tsx';

export function ExternalCommitteeDashboard() {
  const { data: globalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats');
      return res.data.data;
    },
  });

  return (
    <Layout title="External Committee Portal">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Proposal Moderation</h2>
          <p className="text-slate-500 text-sm">Review, prioritize and moderate idea workflow</p>
        </div>
      </div>

      {statsLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="mb-8">
          {/* Row 1: 4 simple status cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {/* 1) Total Ideas */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                  <Folder className="w-4 h-4" />
                </div>
                <p className="text-[13px] text-slate-600 font-semibold">Total Ideas</p>
              </div>
              <p className="text-lg font-bold text-slate-800">{globalStats?.kpis?.totalIdeas || 0}</p>
            </div>

            {/* 2) Requires My Approval */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-orange-50 p-2 rounded-lg text-orange-600">
                  <AlertCircle className="w-4 h-4" />
                </div>
                <p className="text-[13px] text-slate-600 font-semibold">Requires My Approval</p>
              </div>
              <p className="text-lg font-bold text-slate-800">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
            </div>

            {/* 3) External Approved */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-emerald-50 p-2 rounded-lg text-emerald-600">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <p className="text-[13px] text-slate-600 font-semibold">External Approved</p>
              </div>
              <p className="text-lg font-bold text-slate-800">{globalStats?.kpis?.approved || 0}</p>
            </div>

            {/* 4) External Rejected */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-rose-50 p-2 rounded-lg text-rose-600">
                  <XCircle className="w-4 h-4" />
                </div>
                <p className="text-[13px] text-slate-600 font-semibold">External Rejected</p>
              </div>
              <p className="text-lg font-bold text-slate-800">{globalStats?.kpis?.externalRejected || 0}</p>
            </div>
          </div>

          {/* Row 2: 2 compact Nos & Value cards matching top card width */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 5) Internal Committee Head Approved */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-green-50 p-1.5 rounded-lg text-green-600 shrink-0">
                  <IndianRupee className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Internal Committee Head Approved">
                  Internal Committee Head Approved
                </p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.totalAnnualSavings || 0} />
                </div>
              </div>
            </div>

            {/* 6) External Committee Approved */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between h-28">
              <div className="flex items-center space-x-2.5 mb-2">
                <div className="bg-purple-50 p-1.5 rounded-lg text-purple-600 shrink-0">
                  <IndianRupee className="w-3.5 h-3.5" />
                </div>
                <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="External Committee Approved">
                  External Committee Approved
                </p>
              </div>
              <div className="flex justify-between items-end border-t border-slate-100 pt-2 mt-auto">
                <div>
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                  <p className="text-[15px] font-bold text-slate-800">{globalStats?.kpis?.approved || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Value</p>
                  <AmountDisplay amount={globalStats?.kpis?.actualExternalApprovedAmount || 0} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <IdeasFilterTable
        queryKeyPrefix="externalIdeas"
        apiEndpoint="/admin/ideas"
        showExport={true}
        allowDelete={false}
        actionButtonLabel="Evaluate"
      />
    </Layout>
  );
}
